import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Input } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { showToast, ToastType } from '@/helpers';
import i18n from '@/i18n';
import { useUpdateOrganizationCustomResolutionMutation } from '@/services';

const CUSTOM_RESOLUTION_REGEX = /^[1-9]\d{1,4}x[1-9]\d{1,4}$/;

const customResolutionSchema = () =>
  Yup.object().shape({
    customResolution: Yup.string()
      .trim()
      .nullable()
      .matches(CUSTOM_RESOLUTION_REGEX, {
        message: i18n.t('customResolutionInvalid', { ns: 'common' }),
        excludeEmptyString: true
      })
  });

type CustomResolutionFormProps = {
  organizationId: number;
  customResolution?: string | null;
};

export const CustomResolutionForm = ({
  organizationId,
  customResolution
}: CustomResolutionFormProps) => {
  const [t] = useTranslation('common');

  const { mutate: updateCustomResolution, isLoading } =
    useUpdateOrganizationCustomResolutionMutation(queryClient, {
      onSuccess: () => {
        showToast(ToastType.SUCCESS, t('customResolutionUpdated'));
      },
      onError: (error) => {
        showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
      }
    });

  return (
    <div className='mb-6'>
      <div className='mb-4 flex items-center'>
        <Label text={t('customResolution')} type={LABEL_TYPE.H3_BOLD} />
      </div>
      <Formik
        enableReinitialize
        initialValues={{ customResolution: customResolution ?? '' }}
        validationSchema={customResolutionSchema()}
        onSubmit={(values) =>
          updateCustomResolution({
            organizationId,
            customResolution: values.customResolution.trim() || null
          })
        }>
        {({ errors, touched, isValid, dirty }) => (
          <Form className='flex flex-col gap-4 sm:flex-row sm:items-start'>
            <Input
              id='customResolution'
              label={t('customResolution')}
              type='text'
              placeholder='1920x1080'
              width='w-48'
              errorMessage={touched.customResolution ? errors.customResolution : undefined}
              isInvalid={!!(errors.customResolution && touched.customResolution)}
            />
            <Button
              variant={ButtonVariant.PRIMARY}
              type='submit'
              className='sm:mt-7'
              disabled={isLoading || !isValid || !dirty}>
              {t('save')}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};
